import { DeltaArrow } from '@plato/ui-legacy/components/typography/delta-arrow';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { cn } from '@plato/ui/util/classes';

type Props = {
    /** Ratio: 0.15 renders as +15% ('percent') or +15.0 pp ('points'). */
    value: number | undefined;
    unit?: 'percent' | 'points';
    shouldHighlight?: boolean;
};

/**
 * Secondary-line trend chip under a table value: arrow + signed change vs. the
 * same period last year. Renders nothing when there is no meaningful baseline
 * (value undefined).
 */
export function TrendChip({ value, unit = 'percent', shouldHighlight = false }: Props) {
    const locale = useResolvedLocale();

    if (value === undefined) {
        return null;
    }

    const up = value > 0 ? true : value < 0 ? false : null;
    const highlighted = shouldHighlight && up !== null;
    const formatted =
        unit === 'points'
            ? `${new Intl.NumberFormat(locale, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                  signDisplay: 'exceptZero',
              }).format(value * 100)} pp`
            : new Intl.NumberFormat(locale, {
                  style: 'percent',
                  maximumFractionDigits: 0,
                  signDisplay: 'exceptZero',
              }).format(value);

    return (
        <Text
            as="div"
            size="xs"
            className={cn(
                'flex items-center justify-end gap-0.5 tabular-nums',
                !highlighted && 'text-neutral-500',
                highlighted && up && 'text-green-600',
                highlighted && !up && 'text-red-600',
            )}
        >
            <DeltaArrow up={up} className={cn('h-3 w-3', !highlighted && 'text-neutral-400')} />
            {formatted}
        </Text>
    );
}
